import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ScrollPanel } from 'primereact/scrollpanel';
import { classNames } from 'primereact/utils';
import { MutableRefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  NotificationsParams,
  useListNotifications,
  useUpdateNotification,
} from '../../../../../hooks/api/notifications';
import useNotificationContext from '../../../../../hooks/notification/useNotificationContext';
import { NotificationMessage } from '../../../../../shared/enums/notification-message-map';
import { timeAgo } from '../../../../../shared/helpers/time-ago';
import { INotification } from '../../../../../shared/interfaces/INotification';
import styles from './NotificationList.module.css';

export const TOTAL_NOTIFICATIONS_LIST_SIZE = 10;
const MESSAGES_WITHOUT_TIME_AGO = new Set<string>([
  NotificationMessage.CreateRequestSustainableAlternative,
  NotificationMessage.CreateRequestUpdatePcfValues,
  NotificationMessage.CreateRequestPcfShared,
  NotificationMessage.CreateRequestPcfAnswered,
  NotificationMessage.CcmCreateRequest,
]);

const NOTIFICATION_TYPES_TO_REDIRECT_ON_CLICK = new Set<string>([
  NotificationMessage.CreateRequestPcfShared,
  NotificationMessage.CreateRequestUpdatePcfValues,
]);

type NotificationListProps = {
  overlayPanelRef: MutableRefObject<OverlayPanel | null>;
};

const NotificationList = (props: NotificationListProps) => {
  const { overlayPanelRef } = props;
  const { setIsNewNotification } = useNotificationContext();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const [notificationsParams, setNotificationsParams] = useState<NotificationsParams>({
    size: TOTAL_NOTIFICATIONS_LIST_SIZE,
    skip: 0,
  });
  const [loadedNotifications, setLoadedNotifications] = useState<INotification[]>([]);
  const [newNotificationCount, setNewNotificationCount] = useState(0);

  const {
    isLoading: notificationLoading,
    isError,
    data,
  } = useListNotifications(notificationsParams);

  const notifications = data?.data ?? [];
  const pagination = data?.pagination;

  const { mutateAsync: updateNotificationAsync } = useUpdateNotification();

  // Function to handle unread notification click
  const handleUnreadNotification = async (notification: INotification) => {
    await updateNotificationAsync(notification.id);

    // If updating notification status in the database is successful, then:
    const clickedNotificationId = notification.id;

    // Mark this notification as read
    const notifIndex = loadedNotifications.findIndex((notification) => {
      return notification.id === clickedNotificationId;
    });

    // Update notifications in the state
    const updatedNotifications = [...loadedNotifications];
    updatedNotifications[notifIndex].read = true;
    setLoadedNotifications(updatedNotifications);

    // Check if there's any unread notification left
    const numberOfUnreadNotifications = countUnreadNotifications(updatedNotifications);

    setNewNotificationCount(numberOfUnreadNotifications); // For the display "You have XX new notifications"
    setIsNewNotification(numberOfUnreadNotifications > 0); // For the green dot
  };

  const handleNavigation = (notification: INotification) => {
    // If the clicked notification is one of the types to redirect on click, then redirect to the component details page
    if (
      NOTIFICATION_TYPES_TO_REDIRECT_ON_CLICK.has(notification.message) &&
      notification?.details?.productId !== undefined
    ) {
      // Let the default url be component details page
      let url = `/components/${notification.details.productId}/component-details`;

      // If the notification is a PCF update notification and the respective product is a reseller copy (a product, not component),
      // then redirect to product details page
      if (
        notification.message === NotificationMessage.CreateRequestUpdatePcfValues &&
        notification?.details?.isProduct
      ) {
        url = `/products/${notification.details.productId}/product-details`;
      }

      navigate(url);

      // Hide the notifications overlay panel if it's open
      if (overlayPanelRef?.current) {
        overlayPanelRef.current.hide();
      }
    }
  };

  const onNotificationClick = async (notification: INotification) => {
    if (!notification.read) {
      handleUnreadNotification(notification);
    }

    handleNavigation(notification);
  };

  /**
   * This useEffect handles when there's a new response from notifications API;
   * * Combines them into a single array
   * * Sets the unread notification count
   */
  useEffect(() => {
    if (notifications.length === 0) {
      return;
    }

    setLoadedNotifications((prevNotifications) => {
      const prevNotificationsCp = [...prevNotifications]; // Copy the existing notifications

      // Add newly fetched notifications to the existing notifications list
      notifications.forEach((notification) => {
        prevNotificationsCp.push(notification);
      });

      return prevNotificationsCp;
    });

    // Check if there is a new notification about pcf request being answered
    // If yes, invalidate the components so that new component will be fetched
    const hasPcfRequestAnsweredNotification = notifications.some((notification) => {
      return (
        notification.message === NotificationMessage.CreateRequestPcfAnswered && !notification.read
      );
    });

    if (hasPcfRequestAnsweredNotification) {
      queryClient.invalidateQueries({ queryKey: ['components'] });
    }
  }, [notifications]);

  /**
   * This useEffect updates the new notification count and the isNewNotification state
   * whenever loadedNotifications changes.
   */
  useEffect(() => {
    const numberOfUnreadNotifications = countUnreadNotifications(loadedNotifications);

    setNewNotificationCount(numberOfUnreadNotifications); // For the display "You have XX new notifications"
    setIsNewNotification(numberOfUnreadNotifications > 0); // For the green dot
  }, [loadedNotifications, setIsNewNotification]);

  const countUnreadNotifications = (notifications: INotification[]): number => {
    return notifications.filter((val) => {
      return !val.read;
    }).length;
  };

  const loadMoreNotifications = () => {
    setNotificationsParams((params) => {
      return {
        ...params,
        skip: (params.skip ?? 0) + TOTAL_NOTIFICATIONS_LIST_SIZE,
      };
    });
  };

  const { t } = useTranslation();

  const formDescriptionText = (notification: INotification) => {
    let text;

    if (
      notification.message === NotificationMessage.CreateRequestSustainableAlternative ||
      notification.message === NotificationMessage.CreateRequestUpdatePcfValues
    ) {
      // ******** ONLY FOR SUSTAINABLE ALTERNATIVE AND UPDATE PCF VALUES ********
      // Check if metadata exists in the details column:
      // If yes, then use it and translate the rest of the text
      // If not, that means the database entry is created with English text, i.e. don't use metadata, don't translate anything, just show what's written in the details.description
      if (!notification.details.description) {
        if (notification.details.componentName !== undefined) {
          text = t('notifications.requestSustainableAlternative.details', {
            componentName: notification.details.componentName,
          });
        } else if (notification.details.totalRequestsUpdated !== undefined) {
          text = t('notifications.updatePcfValues.details', {
            totalRequestsUpdated: notification.details.totalRequestsUpdated,
          });
        }
        // If the productName is available, use it for the text
        else if (notification.details.productName) {
          text = t('notifications.updatePcf.details', {
            componentName: notification.details.productName,
          });
        }
      } else {
        text = notification.details.description;
      }
    } else if (notification.message === NotificationMessage.CreateRequestPcfAnswered) {
      text = t('notifications.pcfAnswered.details', {
        companyName: notification.details?.companyName,
        productName: notification.details?.productName,
      });
    } else if (notification.message === NotificationMessage.CcmCreateRequest) {
      text = t('notifications.ccmCreateRequest.details', {
        companyName: notification.details?.companyName,
        certificateType: notification.details?.certificateType,
      });
    } else if (notification.message !== NotificationMessage.CreateRequestSustainableAlternative) {
      text = t(`notificationsDialog.${notification.message}`);

      if (
        notification.message === NotificationMessage.CreateRequestAdditionalProduct ||
        notification.message === NotificationMessage.UpdateRequestAdditionalProduct
      ) {
        text += ' ' + notification.details.componentName;
      } else if (notification.message === NotificationMessage.CreateRequestPcfShared) {
        text = t('notifications.pcfShared.details', {
          companyName: notification.details?.companyName,
          componentName: notification.details?.productName,
        });
      }
    }

    return (
      <span
        className={classNames('fs-small', {
          'font-semibold': !notification.read,
        })}
      >
        {text}
      </span>
    );
  };

  const formTimeAgoText = (notification: INotification) => {
    if (!MESSAGES_WITHOUT_TIME_AGO.has(notification.message)) {
      return (
        <div className='flex justify-content-between align-items-end'>
          <span className='fs-small'>{t(`pcfRequest.${notification.details.status}`)}</span>
          <small className='text-grey-100'>{timeAgo(notification.dateCreated)}</small>
        </div>
      );
    }

    return null;
  };

  const renderAllNotitications = () => {
    return (
      <>
        <li className='mb-3 px-4'>
          <span className='fs-small font-semibold'>
            {newNotificationCount > 0 ? (
              <>
                {t('notificationsDialog.youHaveText')}{' '}
                <span className='text-primary'>{newNotificationCount}</span>{' '}
                {t('notificationsDialog.newNotifications')}
              </>
            ) : (
              // eslint-disable-next-line quotes
              <> {t('notificationsDialog.noNotificationText')}</>
            )}
          </span>
        </li>

        {loadedNotifications.map((notification: INotification) => {
          return (
            <li
              data-test-id={notification.id}
              className={classNames(
                'mb-3 p-2 pl-4 border-bottom-1 border-gray-100',
                styles['notification-list-item'],
                {
                  'cursor-pointer':
                    !notification.read || // Only clickable if the notification is unread
                    NOTIFICATION_TYPES_TO_REDIRECT_ON_CLICK.has(notification.message), //  or if it's a clickable notification type
                },
              )}
              key={notification.id}
              onClick={() => {
                onNotificationClick(notification);
              }}
            >
              <div className='grid grid-cols-12 gap-2'>
                <div className='col-11'>
                  <div className='flex flex-column gap-2'>
                    {formDescriptionText(notification)}
                    {formTimeAgoText(notification)}
                  </div>
                </div>
                <div
                  className={classNames(
                    'col-1 border-circle my-auto p-0',
                    styles['new-notification-dot'],
                    {
                      'bg-primary': !notification.read,
                    },
                  )}
                />
              </div>
            </li>
          );
        })}

        {pagination && loadedNotifications.length < pagination.total && (
          <li className='text-center'>
            <Button
              label={t('notificationsDialog.showMore')}
              text
              onClick={loadMoreNotifications}
            />
          </li>
        )}
      </>
    );
  };

  return (
    <>
      <ScrollPanel style={{ width: '100%', height: '250px' }} className='p-0'>
        <ul className='list-none p-0 mx-0 my-3'>
          {notificationLoading && (
            <li>
              <ProgressSpinner className='w-full' />
            </li>
          )}

          {!notificationLoading && !isError && renderAllNotitications()}
        </ul>
      </ScrollPanel>
    </>
  );
};

export default NotificationList;
